import { cn } from '@/lib/utils';
import { Button } from '@/components/base/Button';
import ExitBlueIcon from '@/icons/ExitBlue.svg?react';
import type { LucideIcon } from 'lucide-react';

/**
 * Content interface for NoResultsCard component
 */
export interface NoResultsCardContent {
  headline: string;
  bodyText: string;
  clearButtonLabel: string;
}

/**
 * Props interface for NoResultsCard component
 */
export interface NoResultsCardProps {
  content?: NoResultsCardContent;
  onClearSearch?: () => void;
  /** Optional content rendered between the body text and the clear button */
  children?: React.ReactNode;
  className?: string;
}

/**
 * NoResultsCard - Displayed when a search query returns zero matching resources.
 * Shows a headline, body text, and a "Clear search" button with an X icon.
 *
 * @example
 * ```tsx
 * import { NoResultsCard } from '@/components';
 *
 * <NoResultsCard
 *   content={{
 *     headline: "No resources found",
 *     bodyText: "Try modifying your search with different keywords.",
 *     clearButtonLabel: "Clear search"
 *   }}
 *   onClearSearch={() => handleClearSearch()}
 * />
 * ```
 */
export function NoResultsCard({ content, onClearSearch, children, className }: NoResultsCardProps) {
  if (!content) return null;

  return (
    <div
      className={cn(
        'rounded-[4px] border border-[#EBEBEB] bg-white shadow-[0px_4px_4px_0px_rgba(187,187,187,0.25)]',
        'p-4 sm:p-6 lg:p-10',
        className
      )}
    >
      <h2
        style={{
          fontFamily: "'Source Sans Pro', sans-serif",
          fontWeight: 600,
          fontSize: '24px',
          lineHeight: '32px',
          color: '#000000',
        }}
      >
        {content.headline}
      </h2>

      <p
        className="mt-2"
        style={{
          fontFamily: "'Source Sans Pro', sans-serif",
          fontWeight: 400,
          fontSize: '20px',
          lineHeight: '28px',
          color: '#000000',
        }}
      >
        {content.bodyText}
      </p>

      {children}

      <Button
        variant="secondary"
        size="large"
        content={{
          text: content.clearButtonLabel,
          icon: ExitBlueIcon as unknown as LucideIcon,
        }}
        onClick={onClearSearch}
        className="mt-4"
      />
    </div>
  );
}

NoResultsCard.displayName = 'NoResultsCard';
